import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import type { Block, BlockType, ContentVisibility, Page } from "@/types/database";
import { Json } from "@/integrations/supabase/types";
import { positionBetween, positionForIndex } from "@/lib/block-position";
import { slugify } from "@/lib/slugify";

function arrayMoveLocal<T>(items: T[], from: number, to: number): T[] {
  const copy = items.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
}

// Moving an item only ever needs a position between its new neighbors, so
// this is the one calculation every drag-and-drop reorder (blocks within a
// row, pages within a sibling group) shares — neither ever renumbers
// sibling rows. Generic over the field being reordered on since blocks use
// block_order and pages use page_order.
function computeReorderPosition<T>(items: T[], fromIndex: number, toIndex: number, getOrder: (item: T) => number): number {
  const reordered = arrayMoveLocal(items, fromIndex, toIndex);
  const newIndex = reordered.findIndex((item) => item === items[fromIndex]);
  const before = reordered[newIndex - 1] ? getOrder(reordered[newIndex - 1]) : null;
  const after = reordered[newIndex + 1] ? getOrder(reordered[newIndex + 1]) : null;
  return positionBetween(before, after);
}

type PageUpdatableFields = Pick<
  Page,
  | "title"
  | "slug"
  | "seo_title"
  | "seo_description"
  | "is_published"
  | "visibility"
  | "parent_page_id"
  | "show_in_nav"
  | "page_order"
>;

export function usePages(siteId: string | undefined) {
  const getClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const queryKey = ["pages", siteId] as const;

  const query = useQuery({
    queryKey,
    enabled: Boolean(siteId),
    queryFn: async (): Promise<Page[]> => {
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("pages")
        .select("*")
        .eq("site_id", siteId as string)
        .order("page_order");
      if (error) throw error;
      return (data ?? []) as Page[];
    },
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey });

  const createPage = useMutation({
    mutationFn: async ({
      title,
      parentPageId,
      showInNav,
    }: {
      title: string;
      parentPageId?: string | null;
      showInNav?: boolean;
    }): Promise<Page> => {
      if (!siteId) throw new Error("No site loaded");
      const supabase = await getClient();
      const siblings = (query.data ?? []).filter((page) => page.parent_page_id === (parentPageId ?? null));
      const pageOrder = positionForIndex(siblings.length);
      const { data, error } = await supabase
        .from("pages")
        .insert({
          site_id: siteId,
          title,
          slug: slugify(title),
          page_order: pageOrder,
          parent_page_id: parentPageId ?? null,
          show_in_nav: showInNav ?? true,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Page;
    },
    onSuccess: invalidate,
  });

  const updatePage = useMutation({
    mutationFn: async ({
      pageId,
      updates,
    }: {
      pageId: string;
      updates: Partial<PageUpdatableFields>;
    }): Promise<Page> => {
      const supabase = await getClient();
      // `visibility` predates this repo's last `supabase gen types` refresh
      // (see CLAUDE.md's TypeScript Standards) — stopgap cast, same
      // convention as use-site.ts's `as any` casts, until types are
      // regenerated against the applied migration.
      const { data, error } = await supabase.from("pages").update(updates as any).eq("id", pageId).select().single();
      if (error) throw error;
      return data as Page;
    },
    onSuccess: invalidate,
  });

  const deletePage = useMutation({
    mutationFn: async (pageId: string): Promise<void> => {
      const supabase = await getClient();
      const { error } = await supabase.from("pages").delete().eq("id", pageId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Only one page per site may be home (enforced by a partial unique
  // index — see the migration), so this has to unset the previous home
  // page before setting the new one; doing it the other way round would
  // briefly have two rows with is_home=true and violate that index. Two
  // sequential requests, not a transaction — matches this file's existing
  // multi-step mutations (e.g. addBlockToRow's resize-then-insert).
  const setHomePage = useMutation({
    mutationFn: async (pageId: string): Promise<void> => {
      if (!siteId) throw new Error("No site loaded");
      const supabase = await getClient();
      // `is_home` predates this repo's last `supabase gen types` refresh —
      // same stopgap as visibility above, but the whole builder needs the
      // cast here since the filter (not just the update payload) touches
      // the ungenerated column.
      const { error: unsetError } = await (supabase.from("pages") as any)
        .update({ is_home: false })
        .eq("site_id", siteId)
        .eq("is_home", true);
      if (unsetError) throw unsetError;

      const { error: setError } = await supabase.from("pages").update({ is_home: true } as any).eq("id", pageId);
      if (setError) throw setError;
    },
    onSuccess: invalidate,
  });

  // Reordering is scoped to siblings (pages sharing the same parent) so
  // dragging within "Main Navigation" never disturbs an L2 dropdown's own
  // order, and vice versa.
  const reorderPage = useMutation({
    mutationFn: async ({ pageId, overPageId }: { pageId: string; overPageId: string }): Promise<void> => {
      const pages = query.data ?? [];
      const moving = pages.find((page) => page.id === pageId);
      if (!moving) return;

      const siblings = pages
        .filter((page) => page.parent_page_id === moving.parent_page_id)
        .sort((a, b) => a.page_order - b.page_order);
      const fromIndex = siblings.findIndex((page) => page.id === pageId);
      const toIndex = siblings.findIndex((page) => page.id === overPageId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

      const newPosition = computeReorderPosition(siblings, fromIndex, toIndex, (page) => page.page_order);
      const supabase = await getClient();
      const { error } = await supabase.from("pages").update({ page_order: newPosition }).eq("id", pageId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Drag-and-drop between the Pages panel's "Main Navigation" and "Not
  // Linked" sections — one write that both flips show_in_nav and positions
  // the page among its new section's top-level siblings, so dropping into
  // an empty section (no existing siblings to position against) still
  // lands the page there rather than needing a fallback toggle.
  const moveTopLevelPage = useMutation({
    mutationFn: async ({
      pageId,
      showInNav,
      overPageId,
    }: {
      pageId: string;
      showInNav: boolean;
      overPageId: string | null;
    }): Promise<void> => {
      const pages = query.data ?? [];
      const destination = pages
        .filter((page) => page.parent_page_id === null && page.show_in_nav === showInNav && page.id !== pageId)
        .sort((a, b) => a.page_order - b.page_order);

      const overIndex = overPageId ? destination.findIndex((page) => page.id === overPageId) : -1;
      const before = overIndex > 0 ? destination[overIndex - 1].page_order : null;
      const newOrder =
        overIndex >= 0 ? positionBetween(before, destination[overIndex].page_order) : positionForIndex(destination.length);

      const supabase = await getClient();
      const { error } = await supabase
        .from("pages")
        .update({ show_in_nav: showInNav, page_order: newOrder, parent_page_id: null })
        .eq("id", pageId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    pages: query.data ?? [],
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    saving:
      createPage.isPending ||
      updatePage.isPending ||
      deletePage.isPending ||
      reorderPage.isPending ||
      moveTopLevelPage.isPending ||
      setHomePage.isPending,
    createPage: (title: string, parentPageId?: string | null, showInNav?: boolean) =>
      createPage.mutateAsync({ title, parentPageId, showInNav }),
    updatePage: (pageId: string, updates: Partial<PageUpdatableFields>) => updatePage.mutateAsync({ pageId, updates }),
    deletePage: (pageId: string) => deletePage.mutateAsync(pageId),
    setHomePage: (pageId: string) => setHomePage.mutateAsync(pageId),
    reorderPage: (pageId: string, overPageId: string) => reorderPage.mutateAsync({ pageId, overPageId }),
    moveTopLevelPage: (pageId: string, showInNav: boolean, overPageId: string | null) =>
      moveTopLevelPage.mutateAsync({ pageId, showInNav, overPageId }),
    // Exposed for callers that need the freshly-created rows right after a
    // bulk operation elsewhere invalidates this same query (e.g. the
    // onboarding wizard reading back the pages a template just created) —
    // everything else here reads reactively off `pages` instead.
    refetch: () => query.refetch(),
  };
}

export function useBlocks(pageId: string | undefined) {
  const getClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const queryKey = ["blocks", pageId] as const;

  const query = useQuery({
    queryKey,
    enabled: Boolean(pageId),
    queryFn: async (): Promise<Block[]> => {
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("blocks")
        .select("*")
        .eq("page_id", pageId as string)
        .order("block_order");
      if (error) throw error;
      return (data ?? []) as Block[];
    },
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey });

  const addBlock = useMutation({
    mutationFn: async (blockType: BlockType): Promise<Block> => {
      if (!pageId) throw new Error("No page selected");
      const supabase = await getClient();
      const rowCount = new Set((query.data ?? []).map((block) => block.row_order)).size;
      const order = positionForIndex(rowCount);
      // block_type's generated union predates leadership/unit-identity —
      // same stopgap as visibility, see the comment on updatePage below.
      const { data, error } = await supabase
        .from("blocks")
        .insert({ page_id: pageId, block_type: blockType, content: {}, block_order: order, row_order: order, col_span: 12 } as any)
        .select()
        .single();
      if (error) throw error;
      return data as Block;
    },
    onSuccess: invalidate,
  });

  // Inserts a new block into the same row as an existing one, splitting
  // that block's current column span with the new block (the user then
  // fine-tunes the split via the row's drag handle).
  const addBlockToRow = useMutation({
    mutationFn: async ({ afterBlockId, blockType }: { afterBlockId: string; blockType: BlockType }): Promise<Block> => {
      if (!pageId) throw new Error("No page selected");
      const blocks = query.data ?? [];
      const afterBlock = blocks.find((block) => block.id === afterBlockId);
      if (!afterBlock) throw new Error("Block not found");

      const rowSiblings = blocks
        .filter((block) => block.row_order === afterBlock.row_order)
        .sort((a, b) => a.block_order - b.block_order);
      const afterIndex = rowSiblings.findIndex((block) => block.id === afterBlockId);
      const nextSibling = rowSiblings[afterIndex + 1] ?? null;

      const remainingSpan = Math.max(1, Math.ceil(afterBlock.col_span / 2));
      const newBlockSpan = Math.max(1, afterBlock.col_span - remainingSpan);
      const newBlockOrder = positionBetween(afterBlock.block_order, nextSibling?.block_order ?? null);

      const supabase = await getClient();
      const { error: resizeError } = await supabase
        .from("blocks")
        .update({ col_span: remainingSpan })
        .eq("id", afterBlockId);
      if (resizeError) throw resizeError;

      const { data, error } = await supabase
        .from("blocks")
        .insert({
          page_id: pageId,
          block_type: blockType,
          content: {},
          block_order: newBlockOrder,
          row_order: afterBlock.row_order,
          col_span: newBlockSpan,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data as Block;
    },
    onSuccess: invalidate,
  });

  // Patches the cache with the mutation's own response instead of
  // invalidating (which would trigger a redundant full refetch of every
  // block on the page) — this is the hot path for typing in the edit
  // drawer, so it skips the extra round trip entirely.
  const updateBlockContent = useMutation({
    mutationFn: async ({ blockId, content }: { blockId: string; content: Json }): Promise<Block> => {
      const supabase = await getClient();
      const { data, error } = await supabase.from("blocks").update({ content }).eq("id", blockId).select().single();
      if (error) throw error;
      return data as Block;
    },
    onSuccess: (updatedBlock) => {
      queryClient.setQueryData<Block[]>(queryKey, (current) =>
        current?.map((block) => (block.id === updatedBlock.id ? updatedBlock : block))
      );
    },
  });

  const updateBlockVisibility = useMutation({
    mutationFn: async ({ blockId, visibility }: { blockId: string; visibility: ContentVisibility }): Promise<Block> => {
      const supabase = await getClient();
      // Stopgap cast — see the matching comment on updatePage above.
      const { data, error } = await supabase.from("blocks").update({ visibility } as any).eq("id", blockId).select().single();
      if (error) throw error;
      return data as Block;
    },
    onSuccess: (updatedBlock) => {
      queryClient.setQueryData<Block[]>(queryKey, (current) =>
        current?.map((block) => (block.id === updatedBlock.id ? updatedBlock : block))
      );
    },
  });

  // Deleting the second-to-last block in a row lets the sole survivor
  // reclaim the full row width, rather than leaving a partial-width block
  // stranded next to empty space.
  const deleteBlock = useMutation({
    mutationFn: async (blockId: string): Promise<void> => {
      const blocks = query.data ?? [];
      const target = blocks.find((block) => block.id === blockId);
      const supabase = await getClient();

      const { error } = await supabase.from("blocks").delete().eq("id", blockId);
      if (error) throw error;

      if (target) {
        const rowSiblings = blocks.filter((block) => block.row_order === target.row_order && block.id !== blockId);
        if (rowSiblings.length === 1 && rowSiblings[0].col_span !== 12) {
          const { error: growError } = await supabase.from("blocks").update({ col_span: 12 }).eq("id", rowSiblings[0].id);
          if (growError) throw growError;
        }
      }
    },
    onSuccess: invalidate,
  });

  function distinctRows(blocks: Block[]): number[] {
    return Array.from(new Set(blocks.map((block) => block.row_order))).sort((a, b) => a - b);
  }

  async function moveRowOrder(blocks: Block[], rowOrder: number, newRowOrder: number): Promise<void> {
    const supabase = await getClient();
    const rowBlockIds = blocks.filter((block) => block.row_order === rowOrder).map((block) => block.id);
    const { error } = await supabase.from("blocks").update({ row_order: newRowOrder }).in("id", rowBlockIds);
    if (error) throw error;
  }

  // Moves an entire row (every block sharing its row_order) up or down
  // relative to the other rows on the page — vertical position is always a
  // row-level concept, block_order only orders blocks left-to-right within
  // one row.
  const moveRow = useMutation({
    mutationFn: async ({ blockId, direction }: { blockId: string; direction: "up" | "down" }): Promise<void> => {
      const blocks = query.data ?? [];
      const block = blocks.find((item) => item.id === blockId);
      if (!block) return;

      const rows = distinctRows(blocks);
      const index = rows.indexOf(block.row_order);
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (index < 0 || targetIndex < 0 || targetIndex >= rows.length) return;

      const reordered = arrayMoveLocal(rows, index, targetIndex);
      const newIndex = reordered.indexOf(block.row_order);
      const newRowOrder = positionBetween(reordered[newIndex - 1] ?? null, reordered[newIndex + 1] ?? null);
      await moveRowOrder(blocks, block.row_order, newRowOrder);
    },
    onSuccess: invalidate,
  });

  const reorderRow = useMutation({
    mutationFn: async ({ blockId, overBlockId }: { blockId: string; overBlockId: string }): Promise<void> => {
      const blocks = query.data ?? [];
      const block = blocks.find((item) => item.id === blockId);
      const overBlock = blocks.find((item) => item.id === overBlockId);
      if (!block || !overBlock || block.row_order === overBlock.row_order) return;

      const rows = distinctRows(blocks);
      const fromIndex = rows.indexOf(block.row_order);
      const toIndex = rows.indexOf(overBlock.row_order);
      if (fromIndex < 0 || toIndex < 0) return;

      const reordered = arrayMoveLocal(rows, fromIndex, toIndex);
      const newIndex = reordered.indexOf(block.row_order);
      const newRowOrder = positionBetween(reordered[newIndex - 1] ?? null, reordered[newIndex + 1] ?? null);
      await moveRowOrder(blocks, block.row_order, newRowOrder);
    },
    onSuccess: invalidate,
  });

  // Persists a drag-resize of the handle between two adjacent blocks in the
  // same row — the pure column math (clamping, preserving the row's total
  // span) lives in src/lib/grid-resize.ts; this just writes the result.
  const resizeRow = useMutation({
    mutationFn: async ({
      leftBlockId,
      rightBlockId,
      leftSpan,
      rightSpan,
    }: {
      leftBlockId: string;
      rightBlockId: string;
      leftSpan: number;
      rightSpan: number;
    }): Promise<void> => {
      const supabase = await getClient();
      const { error: leftError } = await supabase.from("blocks").update({ col_span: leftSpan }).eq("id", leftBlockId);
      if (leftError) throw leftError;
      const { error: rightError } = await supabase.from("blocks").update({ col_span: rightSpan }).eq("id", rightBlockId);
      if (rightError) throw rightError;
    },
    onSuccess: invalidate,
  });

  return {
    blocks: query.data ?? [],
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    saving:
      addBlock.isPending ||
      addBlockToRow.isPending ||
      updateBlockContent.isPending ||
      updateBlockVisibility.isPending ||
      deleteBlock.isPending ||
      moveRow.isPending ||
      reorderRow.isPending ||
      resizeRow.isPending,
    addBlock: (blockType: BlockType) => addBlock.mutateAsync(blockType),
    addBlockToRow: (afterBlockId: string, blockType: BlockType) => addBlockToRow.mutateAsync({ afterBlockId, blockType }),
    updateBlockContent: (blockId: string, content: Json) => updateBlockContent.mutateAsync({ blockId, content }),
    updateBlockVisibility: (blockId: string, visibility: ContentVisibility) =>
      updateBlockVisibility.mutateAsync({ blockId, visibility }),
    deleteBlock: (blockId: string) => deleteBlock.mutateAsync(blockId),
    moveRow: (blockId: string, direction: "up" | "down") => moveRow.mutateAsync({ blockId, direction }),
    reorderRow: (blockId: string, overBlockId: string) => reorderRow.mutateAsync({ blockId, overBlockId }),
    resizeRow: (leftBlockId: string, rightBlockId: string, leftSpan: number, rightSpan: number) =>
      resizeRow.mutateAsync({ leftBlockId, rightBlockId, leftSpan, rightSpan }),
  };
}
